// `activities`: Swift `Activity` (GroundTeam/Models/Activity.swift): visits, calls, notes, tasks and the system events
// the app writes itself (`created`, `stageChange`, `assignment`). Decoding template `Activity(type: .note)`
// (GroundTeam/Sync/ResilientDecoding.swift:110), so a payload without `type` reads as a note.

import { ACTIVITY_STATUSES, ACTIVITY_TYPES, type ActivityStatus, type ActivityType } from '../enums';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import { COORDINATE, type CoordinatePayload } from './common';
import {
  BOOL,
  DATE,
  DOUBLE,
  INT,
  STRING,
  UUID,
  arrayOf,
  createModel,
  decodeModel,
  enumOf,
  field,
  nowWire,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';
import { DEFAULT_VERIFICATION_RADIUS_METERS } from './settings';

export interface ActivityPayload extends Passthrough {
  id: string;
  recordId?: string;
  type: ActivityType;
  status: ActivityStatus;
  title: string;
  notes: string;
  /** VisitResult id for visits. */
  resultId?: string;
  /** Free-text outcome for calls ("Left voicemail"). */
  outcome: string;
  userId?: string;
  createdAt: string;
  scheduledAt?: string;
  completedAt?: string;
  /** Int. */
  durationMinutes?: number;
  /** Int. */
  reminderMinutesBefore?: number;
  /** Where the rep was when logging (verified visits). */
  location?: CoordinatePayload;
  distanceFromRecordMeters?: number;
  /** Company verification radius captured when the visit was logged (meters). */
  verificationRadius?: number;
  attachmentIds: string[];
  /** Stage changes / assignments: human-readable "from → to". */
  fromValue: string;
  toValue: string;
  routeId?: string;
  isFromAI: boolean;
}

/** Stored properties of Activity in declaration order (none is local-only; `isVerified` is computed). */
export const ACTIVITY_KEYS = [
  'id',
  'recordId',
  'type',
  'status',
  'title',
  'notes',
  'resultId',
  'outcome',
  'userId',
  'createdAt',
  'scheduledAt',
  'completedAt',
  'durationMinutes',
  'reminderMinutesBefore',
  'location',
  'distanceFromRecordMeters',
  'verificationRadius',
  'attachmentIds',
  'fromValue',
  'toValue',
  'routeId',
  'isFromAI',
] as const satisfies readonly (keyof Known<ActivityPayload>)[];

export const ACTIVITY_FIELDS: FieldMap<ActivityPayload> = {
  id: field.strict(UUID),
  recordId: field.opt(UUID),
  type: field.req(enumOf('ActivityType', ACTIVITY_TYPES), () => 'note'),
  status: field.req(enumOf('ActivityStatus', ACTIVITY_STATUSES), () => 'completed'),
  title: field.req(STRING, () => ''),
  notes: field.req(STRING, () => ''),
  resultId: field.opt(UUID),
  outcome: field.req(STRING, () => ''),
  userId: field.opt(UUID),
  createdAt: field.req(DATE, nowWire),
  scheduledAt: field.opt(DATE),
  completedAt: field.opt(DATE),
  durationMinutes: field.opt(INT),
  reminderMinutesBefore: field.opt(INT),
  location: field.opt(COORDINATE),
  distanceFromRecordMeters: field.opt(DOUBLE),
  verificationRadius: field.opt(DOUBLE),
  attachmentIds: field.req(arrayOf(UUID), () => []),
  fromValue: field.req(STRING, () => ''),
  toValue: field.req(STRING, () => ''),
  routeId: field.opt(UUID),
  isFromAI: field.req(BOOL, () => false),
};

export function decodeActivity(raw: JsonObject, envelopeId?: string): Decoded<ActivityPayload> {
  return decodeModel(ACTIVITY_FIELDS, raw, {}, envelopeId);
}

/** Swift's memberwise init needs `type`; everything else has a default. */
export type ActivityInit = Partial<Known<ActivityPayload>> & { type: ActivityType };

export function newActivity(init: ActivityInit): ActivityPayload {
  const defaults = {
    id: newId(),
    status: 'completed',
    title: '',
    notes: '',
    outcome: '',
    createdAt: nowWire(),
    attachmentIds: [],
    fromValue: '',
    toValue: '',
    isFromAI: false,
  };
  return createModel(ACTIVITY_FIELDS, defaults, init, 'activities');
}

/**
 * `Activity.isVerified`: the single rule for a verified visit, a visit logged within the radius captured at log time
 * (the company default for legacy visits without one). Every screen and report uses this, never the live setting.
 */
export function isVerifiedVisit(
  a: Pick<ActivityPayload, 'type' | 'distanceFromRecordMeters' | 'verificationRadius'>,
): boolean {
  if (a.type !== 'visit' || a.distanceFromRecordMeters === undefined) return false;
  return a.distanceFromRecordMeters <= (a.verificationRadius ?? DEFAULT_VERIFICATION_RADIUS_METERS);
}

/** `Activity.effectiveDate`: completion, else schedule, else creation time (payload string). */
export function activityEffectiveDate(a: Pick<ActivityPayload, 'completedAt' | 'scheduledAt' | 'createdAt'>): string {
  return a.completedAt ?? a.scheduledAt ?? a.createdAt;
}
